import React, { useState, useRef, useEffect } from "react";
import { Amplitude, LogOnMount, useAmplitude } from "react-amplitude-hooks";
import styled from "@emotion/styled";
import {
  Section,
  Label,
  InputText,
  Title,
  TitleBox,
  ButtonRound,
  HistoryContainer
} from "style/components/";
import { TravelContainerProps } from "components/Travel/interface";
import { mobileWithhyphen, getVh } from "components/Travel/function";
import { checkValidity } from "components/Travel/valid";
import { alertText } from "discription";

import PopupMini from "components/Common/Mobile/PopupMini";
import SubscriptionFooter from "./footer";

const HistoryInquery: React.FunctionComponent<TravelContainerProps> = ({
  history,
  match
}) => {
  const { logEvent } = useAmplitude();
  const [mobile, setMobile] = useState("");
  const [validMobile, setValidMobile] = useState(false);
  const [certificate, setCertificate] = useState("");
  const [isCertificateSuccess, setIsCertificateSuccess] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [key, setKey] = useState("");
  const [isAlertModal, setAlertModal] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: "",
    content: ""
  });

  const inputMobileEl = useRef<HTMLInputElement>(null);
  const inputCertificateEl = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getVh();
  });

  const resetMobile = () => {
    setMobile("");
    setValidMobile(false);
    setCertificate("");
    setIsCertificateSuccess(false);
    setKey("");
  };

  const inputMobile = (value: string) => {
    if (isCertificateSuccess) {
      setIsCertificateSuccess(false);
      setKey("");
      setCertificate("");
    }
    const newMobile = mobileWithhyphen(value, mobile);
    setMobile(newMobile);
    if (
      newMobile.length >= 12 &&
      checkValidity(newMobile.replace(/-/gi, ""), { isMobile: true })
    ) {
      setValidMobile(true);
    } else {
      setValidMobile(false);
    }
  };

  const inputCertificate = (value: string) => {
    let certificateNumber = value.trim().replace(/[^0-9]/gi, "");
    if (certificateNumber.length > 6) {
      certificateNumber = certificateNumber.substr(0, 6);
    }
    setCertificate(certificateNumber);
  };

  const submitMobile = () => {
    const data = {
      mobile: mobile.replace(/-/gi, "")
    };
    setIsCertificateSuccess(true);
    fetch(`${process.env.REACT_APP_PARTNER_API}/sms/v1/auth`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(data)
    })
      .then(response => response.json())
      .then(json => {
        if (json.code === "0000") {
          setKey(json.data);
          if (inputCertificateEl && inputCertificateEl.current) {
            inputCertificateEl.current.focus();
          }
        } else {
          setIsCertificateSuccess(false);
          openAlertModal(alertText.apiError.header, alertText.apiError.body);
        }
      })
      .catch(err => {
        setIsCertificateSuccess(false);
        openAlertModal(alertText.apiError.header, alertText.apiError.body);
      });
  };
  const submitCertificate = () => {
    setIsSubmit(true);
    if (mobile === "" || certificate === "") {
      setIsSubmit(false);
      openAlertModal(
        alertText.mobileRequest.header,
        alertText.mobileRequest.body
      );
      logEvent("history_check_button", {
        validation: alertText.mobileRequest.body
      });
      return false;
    }

    const data = {
      key: key,
      productId: 4,
      sponsorCode: "myrealtrip",
      value: certificate
    };
    fetch(`${process.env.REACT_APP_PARTNER_API}/sms/v1/check`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(data)
    })
      .then(response => response.json())
      .then(json => {
        if (json.code === "0000") {
          const subscriptionList = json.data.filter(
            (originalCard: any) =>
              originalCard.marketPaymentInfo.status === "paid"
          );
          if (subscriptionList.length > 0) {
            sessionStorage.setItem(
              "subscription",
              JSON.stringify(subscriptionList)
            );
            history.push("/travel/mrt/subscription/list");
            logEvent("history_check_button");
          } else {
            setIsSubmit(false);
            resetMobile();
            openAlertModal(
              alertText.subscriptionEmpty.header,
              alertText.subscriptionEmpty.body
            );
            logEvent("history_check_button", {
              validation: alertText.subscriptionEmpty.body
            });
          }
        } else {
          setIsSubmit(false);
          openAlertModal(
            alertText.subscriptionCodeNotMatch.header,
            alertText.subscriptionCodeNotMatch.body
          );
          logEvent("history_check_button", {
            validation: alertText.subscriptionCodeNotMatch.body
          });
        }
      })
      .catch(err => {
        setIsSubmit(false);
        openAlertModal(alertText.apiError.header, alertText.apiError.body);
      });
  };

  const openAlertModal = (title: string, content: string) => {
    setModalContent({
      title: title,
      content: content
    });
    setAlertModal(true);
  };
  const onChangeModalConfirm = () => {
    setAlertModal(false);
  };

  return (
    <Amplitude>
      <LogOnMount eventType="history_check" />
      <HistoryContainer>
        <TitleBox>
          <Title>가입내역 조회하기</Title>
          <p className="desc">2019.08.01 이후 가입내역 조회만 가능합니다.</p>
        </TitleBox>
        <HistoryContent>
          <Section className="historySection1">
            <Label>휴대전화번호</Label>
            <InputGroup>
              <InputText
                data-error={false}
                type="tel"
                value={mobile}
                name="mobile"
                onChange={e => inputMobile(e.target.value)}
                pattern="[0-9]*"
                placeholder="예) 01023456789"
                ref={inputMobileEl}
                className="formControl"
              />
              <button
                className="inputGroupAppend"
                onClick={() => {
                  logEvent("history_phone_button");
                  submitMobile();
                }}
                disabled={!validMobile || isCertificateSuccess}
              >
                인증요청
              </button>
            </InputGroup>
            <div>
              <Label>인증번호</Label>
              <InputText
                data-error={false}
                type="tel"
                value={certificate}
                name="certificate"
                onChange={e => inputCertificate(e.target.value)}
                pattern="[0-9]*"
                placeholder="6자리 숫자"
                ref={inputCertificateEl}
                disabled={!isCertificateSuccess}
                className={`${!validMobile ? "disabled" : ""}`}
              />
              <ButtonRound
                type="button"
                onClick={submitCertificate}
                className="find"
                disabled={isSubmit}
              >
                조회하기
              </ButtonRound>
            </div>
          </Section>
        </HistoryContent>
        {isAlertModal && (
          <PopupMini
            hasCancel={false}
            title={modalContent.title}
            content={modalContent.content}
            onOk={onChangeModalConfirm}
          />
        )}
      </HistoryContainer>
      <SubscriptionFooter hasLine={true} />
    </Amplitude>
  );
};

const HistoryContent = styled.div`
  .historySection1 {
    padding-bottom: 30px;
  }

  .goHistoryArrow img {
    margin-top: -2px;
    margin-left: 5px;
    vertical-align: middle;
  }

  .subscriptionButton {
    width: 100%;
    height: 40px;
    border-radius: 5px;
    background-color: #2b96ed;
    color: #ffffff;
  }

  .find {
    margin-top: 40px;
  }
`;

const InputGroup = styled.div`
  display: flex;

  .formControl {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-right: 0;
  }

  .inputGroupAppend {
    flex: 82px 0 0;
    height: 48px;
    border-top-right-radius: ${props => props.theme.radius};
    border-bottom-right-radius: ${props => props.theme.radius};
    border: 1px solid ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.white};
    color: ${props => props.theme.colors.primary};
    font-size: ${props => props.theme.text.sizeFont};
    font-weight: ${props => props.theme.weight.bold};

    :disabled {
      border: solid 1px ${props => props.theme.input.colorBorder};
      color: ${props => props.theme.input.colorBorder};
    }
  }
`;

export default HistoryInquery;
